<!-- Подвал приложения - Organism -->
<template>
  <footer class="bg-black border-t border-white/10">
    <div class="mx-auto max-w-7xl px-6 init">
      <!-- Основной контент футера -->
      <div class="footer-content flex lg:flex-row justify-between gap-12 lg:gap-16 mb-12">
        <!-- Блок 1: Логотип и слоган -->
        <div class="footer-block-logo flex-shrink-0 lg:max-w-md">
          <img src="/images/block/footer/touch-logo-white.png" alt="TOUCH" class="h-[33px] w-auto mb-8"
            loading="lazy" />

          <h3 class="footer-subtitle">
            Оставь свой отпечаток<br>
            в музыкальной индустрии
          </h3>
        </div>

        <!-- Блок 2: Навигация -->
        <div class="footer-block-nav">
          <h4 class="footer-title">
            Главные страницы
          </h4>

          <nav class="space-y-3 navigation-items">
            <a v-for="item in navigationItems" :key="item.href" :href="item.href"
              class="block text-white font-light hover:text-white/80 transition-colors">
              {{ item.label }}
            </a>
          </nav>
        </div>

        <!-- Блок 3: Контакты и кнопка -->
        <div class="footer-block-contact flex flex-col justify-between">
          <div>
            <a :href="`mailto:${contactInfo.email}`"
              class="text-white font-light hover:text-white/80 transition-colors mb-6 block">
              {{ contactInfo.email }}
            </a>

            <!-- Социальные сети -->
            <div class="flex gap-3 mb-6">
              <a v-for="social in contactInfo.socials" :key="social.name" :href="social.href" :aria-label="social.name"
                class="h-[37.5px] w-[37.5px] bg-white color-white border border-white/20 rounded-full flex items-center justify-center rotate-45hover:bg-white/20 transition-colors"
                target="_blank" rel="noopener noreferrer">
                <img :src="social.icon" :alt="social.name" class="w-5 h-5" loading="lazy" />
              </a>
            </div>
          </div>

          <!-- Кнопка "Вернуться наверх" -->
          <BaseButton tone="white" class="back-to-top-btn" @click="scrollToTop">
            <span>Вернуться наверх</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="11" viewBox="0 0 10 11" fill="none">
              <path
                d="M0.772284 5.40611L4.08778 2.09061V10.0399L5.17982 10.0399L5.17982 2.09061L8.49531 5.40611L9.26762 4.63381L4.6338 -9.74935e-06L-1.91303e-05 4.63381L0.772284 5.40611Z"
                fill="currentColor" />
            </svg>
          </BaseButton>
        </div>
      </div>

      <!-- Нижняя часть -->
      <div
        class="footer-bottom flex flex-col md:flex-row justify-between items-center gap-4 pt-8 border-t border-white/10">
        <p class="footer-copyright">
          {{ copyright }}
        </p>

        <p class="footer-design-credit">
          {{ designCredit }}
        </p>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import BaseButton from '@/components/atoms/BaseButton.vue'
import type { NavItem as NavItemType, ContactInfo } from '@/lib/types'

interface Props {
  /** Слоган */
  tagline?: string
  /** Описание */
  description?: string
  /** Элементы навигации */
  navigationItems?: NavItemType[]
  /** Контактная информация */
  contactInfo?: ContactInfo
  /** Копирайт */
  copyright?: string
  /** Информация о дизайне */
  designCredit?: string
}

withDefaults(defineProps<Props>(), {
  tagline: 'Оставь свой отпечаток в музыкальной индустрии',
  description: 'Мультифункциональное приложение для артистов в Telegram. Загружай треки, получай роялти, развивай карьеру.',
  navigationItems: () => [
    { label: 'Возможности', href: '#features-section' },
    { label: 'Приложение', href: '#app' },
    { label: 'Кейсы', href: '#cases' },
    { label: 'Тарифы', href: '#pricing' },
    { label: 'FAQ', href: '#faq' }
  ],
  contactInfo: () => ({
    email: 'hello@touch.com',
    socials: [
      { name: 'VK', href: '#', icon: '/images/block/footer/vk.png' },
      { name: 'Telegram', href: '#', icon: '/images/block/footer/tg.png' },
      { name: 'Дзен', href: '#', icon: '/images/block/footer/dzen.png' },
      { name: 'YouTube', href: '#', icon: '/images/block/footer/yt.png' }
    ]
  }),
  copyright: '© 2025 TOUCH DISTRIBUTION',
  designCredit: 'DESIGN BY ILYA SODEROLOV'
})

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}
</script>


<style scoped>
.footer-subtitle {
  background: linear-gradient(100deg, #FFF 40.34%, rgba(255, 255, 255, 0.00) 135.88%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 32px;
  font-style: normal;
  font-weight: 500;
  line-height: 101%;
  /* 32.32px */
}

footer .init {
  padding-top: 60px;
  padding-bottom: 60px;
}

.footer-copyright,
.footer-design-credit {
  color: #FFF;
  text-align: center;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  text-transform: uppercase;
  margin-top: 36px;
}

.navigation-items {
  display: flex;
  flex-direction: column;
  gap: 19px;
}

.footer-title {
  color: #FFF;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 100%;
  text-transform: uppercase;
  margin-bottom: 19px;
}

.navigation-items a {
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 100%;
}

/* Мобильная версия */
@media (max-width: 1023px) {
  .footer-content {
    flex-direction: column;
    gap: 44px;
  }

  .footer-block-logo {
    order: 1;
  }

  .footer-block-nav {
    order: 2;
  }

  .footer-block-contact {
    order: 3;
  }

  /* Кнопка на всю ширину */
  .back-to-top-btn {
    width: 100%;
    justify-content: space-between;
  }

  .back-to-top-btn svg {
    flex-shrink: 0;
  }

  .footer-bottom {
    text-align: center;
  }

  .footer-copyright,
  .footer-design-credit {
    text-align: center;
    width: 100%;
    margin-top: 0;
  }
}

@media (min-width: 768px) {
  .footer-copyright,
  .footer-design-credit {
    margin-top: 36px;
  }
}

/* Десктопная версия */
@media (min-width: 1024px) {
  .footer-copyright {
    text-align: left;
  }

  .footer-design-credit {
    text-align: right;
  }
}
</style>